/**
 * Bestek (repair quote) constants shared by frontend and backend.
 */

/** Lifecycle: draft (service only) → proposed (customer can see) → answered. */
const BESTEK_STATUS = {
  DRAFT: "draft",
  PROPOSED: "proposed",
  ANSWERED: "answered",
};

/**
 * standard: quote before the repair.
 * after_repair: repaired during diagnosis; hours = diagnose, materials = repair,
 *   always herstelbaar, customer still chooses.
 * administrative: done and decided; service fills in the choice for the documents.
 */
const BESTEK_TYPE = {
  STANDARD: "standard",
  AFTER_REPAIR: "after_repair",
  ADMINISTRATIVE: "administrative",
};

const BESTEK_TYPE_LABELS = {
  [BESTEK_TYPE.STANDARD]: "Bestek",
  [BESTEK_TYPE.AFTER_REPAIR]: "Bestek na herstel",
  [BESTEK_TYPE.ADMINISTRATIVE]: "Administratief bestek",
};

const getBestekType = (bestek) =>
  Object.values(BESTEK_TYPE).includes(bestek?.bestekType)
    ? bestek.bestekType
    : BESTEK_TYPE.STANDARD;

/** Repair already done: cost split by task hours / materials, no FinERP status change. */
const isRepairedBestekType = (bestek) =>
  getBestekType(bestek) !== BESTEK_TYPE.STANDARD;

const BESTEK_STATUS_LABELS = {
  [BESTEK_STATUS.DRAFT]: "Concept",
  [BESTEK_STATUS.PROPOSED]: "Voorgesteld aan klant",
  [BESTEK_STATUS.ANSWERED]: "Klant beantwoord",
};

/** Planning tag on SQLLopendeHRB when customer answered. */
const BESTEK_ANSWERED_TAG = "BESTEKANSWERED";

/** Planning tag when customer reported incompleteness (not a repair decision). */
const BESTEK_INCOMPLETE_TAG = "BESTEKINCOMPLETE";

const BESTEK_OUTCOME = {
  REPAIRABLE: "repairable",
  UNREPAIRABLE: "unrepairable",
};

const BESTEK_DECISION = {
  REPAIR: "repair",
  BUY_NEW: "buy_new",
  NO_REPAIR: "no_repair",
};

/** Per-raming action in the customer choice UI. */
const BESTEK_RAMING_ACTION = {
  REPAIR: "repair",
  BUY_NEW: "buy_new",
  /** Optional ramingen only — do nothing for this raming. */
  SKIP: "skip",
};

const BESTEK_DISPOSITION = {
  RETURN: "return",
  /** Klant haalt het toestel zelf af: geen transportkosten. */
  PICKUP: "pickup",
  DESTROY: "destroy",
  KEEP: "keep",
};

/** Per raming line — herstelniveau 1/2/3 (stored as moet / mag / kan). */
const BESTEK_LINE_REQUIREMENT = {
  /** Niveau 1: werking van het toestel */
  MOET: "moet",
  /** Niveau 2: conformiteit of veiligheid (geadviseerd) */
  MAG: "mag",
  /** Niveau 3: algemeen onderhoud (mogelijk) */
  KAN: "kan",
};

/**
 * FinERP gsm_status.status_tla on herstramuren.type_status_obj (category 23424184.0079).
 * 1MO = Vereist / werking · 2AD = Geadviseerd / veiligheid · 3KN = Mogelijk / onderhoud.
 */
const BESTEK_RAMUTYPE = {
  MOET: "1MO",
  ADVIES: "2AD",
  KAN: "3KN",
};

const BESTEK_RAMUTYPE_TO_REQUIREMENT = {
  [BESTEK_RAMUTYPE.MOET]: BESTEK_LINE_REQUIREMENT.MOET,
  [BESTEK_RAMUTYPE.ADVIES]: BESTEK_LINE_REQUIREMENT.MAG,
  [BESTEK_RAMUTYPE.KAN]: BESTEK_LINE_REQUIREMENT.KAN,
};

const mapRamuTypeToLineRequirement = (ramuType) => {
  const key = String(ramuType || "")
    .trim()
    .toUpperCase();
  return (
    BESTEK_RAMUTYPE_TO_REQUIREMENT[key] || BESTEK_LINE_REQUIREMENT.MOET
  );
};

const BESTEK_LINE_REQUIREMENT_LABELS = {
  [BESTEK_LINE_REQUIREMENT.MOET]: "Vereist",
  [BESTEK_LINE_REQUIREMENT.MAG]: "Geadviseerd",
  [BESTEK_LINE_REQUIREMENT.KAN]: "Mogelijk",
};

const BESTEK_LINE_REQUIREMENT_DESCRIPTIONS = {
  [BESTEK_LINE_REQUIREMENT.MOET]:
    "Herstelwerkzaamheden welke de werking van het toestel beïnvloeden",
  [BESTEK_LINE_REQUIREMENT.MAG]:
    "Herstelwerkzaamheden welke de conformiteit of veiligheid van het toestel beïnvloeden",
  [BESTEK_LINE_REQUIREMENT.KAN]:
    "Herstelwerkzaamheden welke kunnen worden aanzien als algemeen onderhoud",
};

/** Badge colors: moet = blue, geadviseerd = orange, mogelijk = dark gold. */
const BESTEK_LINE_REQUIREMENT_BADGE = {
  [BESTEK_LINE_REQUIREMENT.MOET]: {
    bg: "primary",
    text: undefined,
    style: undefined,
  },
  [BESTEK_LINE_REQUIREMENT.MAG]: {
    bg: undefined,
    text: undefined,
    style: { backgroundColor: "#fd7e14", color: "#fff" },
  },
  [BESTEK_LINE_REQUIREMENT.KAN]: {
    bg: undefined,
    text: undefined,
    style: { backgroundColor: "#8B6914", color: "#fff" },
  },
};

const getBestekRequirementBadgeProps = (requirement) =>
  BESTEK_LINE_REQUIREMENT_BADGE[requirement] ||
  BESTEK_LINE_REQUIREMENT_BADGE[BESTEK_LINE_REQUIREMENT.MOET];

/** HRF substatuses where the customer waits on bestek answer. */
const BESTEK_WAIT_STATUSES = [
  { status: 2, subStatus: 10 }, // Wachten kl. Onherstelbaar (intern)
  { status: 2, subStatus: 15 }, // Wachten kl. Herstelbaar (intern)
  { status: 4, subStatus: 15 }, // Wachten kl. Onherstelbaar (leverancier)
  { status: 4, subStatus: 20 }, // Wachten kl. Herstelbaar (leverancier)
];

/** Status where service can create / edit a bestek. */
const BESTEK_CREATE_STATUS = { status: 2, subStatus: 1 }; // Bestek opmaken
/** Supplier path has no "Bestek opmaken": the supplier quote comes in on Verzonden. */
const BESTEK_SUPPLIER_CREATE_STATUS = { status: 4, subStatus: 10 }; // Verzonden
const BESTEK_CREATE_STATUSES = [
  BESTEK_CREATE_STATUS,
  BESTEK_SUPPLIER_CREATE_STATUS,
];

/** FinERP statuses after customer answered (intern + leverancier). */
const BESTEK_ANSWERED_STATUSES = [
  { status: 2, subStatus: 20 }, // Antwoord klant - mag hersteld worden (intern)
  { status: 2, subStatus: 25 }, // Antwoord klant - niet herstellen (intern)
  { status: 4, subStatus: 25 }, // Antwoord kl. mag hersteld worden (leverancier)
  { status: 4, subStatus: 30 }, // Antwoord kl. niet herstellen (leverancier)
];

const isBestekWaitStatus = (statusNr, subStatusNr) =>
  BESTEK_WAIT_STATUSES.some(
    (s) => s.status === Number(statusNr) && s.subStatus === Number(subStatusNr)
  );

const isBestekAnsweredStatus = (statusNr, subStatusNr) =>
  BESTEK_ANSWERED_STATUSES.some(
    (s) => s.status === Number(statusNr) && s.subStatus === Number(subStatusNr)
  );

const isBestekCreateStatus = (statusNr, subStatusNr) =>
  BESTEK_CREATE_STATUSES.some(
    (s) => s.status === Number(statusNr) && s.subStatus === Number(subStatusNr)
  );

/** Create status on the HRB's own path: supplier (4) or intern (2). */
const getBestekCreateTarget = (hrb = {}) =>
  Number(hrb.HERSTATUSNR) === 4
    ? BESTEK_SUPPLIER_CREATE_STATUS
    : BESTEK_CREATE_STATUS;

/**
 * Document reference for emails/PDF: HRB{DOKNR}.{VERSION}.
 * Version defaults to currentPublishedVersion, then 1.
 */
const formatBestekDocumentRef = (bestek, versionOverride = null) => {
  const doknr = bestek?.DOKNR != null ? String(bestek.DOKNR) : "";
  const version =
    versionOverride != null && versionOverride !== ""
      ? Number(versionOverride)
      : Number(bestek?.currentPublishedVersion) || 1;
  const versionLabel = Number.isFinite(version) && version > 0 ? version : 1;
  return `HRB${doknr}.${versionLabel}`;
};

/** Locked for edits once the customer (or employee on their behalf) chose. */
const hasBestekCustomerChoice = (bestek) =>
  Boolean(bestek?.customerChoice?.decision);

/**
 * True when the customer has an unresolved incompleteness report.
 * Resolved via manual service action or by publishing a new version; a newer
 * report after that reopens the questioning.
 */
const hasOpenBestekIncompleteness = (bestek) => {
  if (hasBestekCustomerChoice(bestek)) return false;
  const reports = Array.isArray(bestek?.incompletenessReports)
    ? bestek.incompletenessReports
    : [];
  if (reports.length === 0) return false;
  const resolvedMs = bestek?.incompletenessResolvedAt
    ? new Date(bestek.incompletenessResolvedAt).getTime()
    : NaN;
  if (!Number.isFinite(resolvedMs)) return true;
  const latestReportMs = reports.reduce((max, report) => {
    const t = report?.reportedAt ? new Date(report.reportedAt).getTime() : 0;
    return Math.max(max, Number.isFinite(t) ? t : 0);
  }, 0);
  return latestReportMs > resolvedMs;
};

/**
 * Content is locked after customer answer, unless service reopened via Bestek opmaken.
 */
const isBestekContentLocked = (bestek, statusNr, subStatusNr) => {
  if (!hasBestekCustomerChoice(bestek)) return false;
  return !isBestekCreateStatus(statusNr, subStatusNr);
};

/**
 * Resolve lifecycle status from stored field or legacy isSubmitted/customerChoice.
 */
const getBestekStatus = (bestek) => {
  if (!bestek) return BESTEK_STATUS.DRAFT;
  if (
    bestek.status &&
    Object.values(BESTEK_STATUS).includes(bestek.status)
  ) {
    return bestek.status;
  }
  if (hasBestekCustomerChoice(bestek)) return BESTEK_STATUS.ANSWERED;
  if (bestek.isSubmitted === true) return BESTEK_STATUS.PROPOSED;
  return BESTEK_STATUS.DRAFT;
};

const isBestekVisibleToCustomer = (bestek) => {
  const status = getBestekStatus(bestek);
  return (
    status === BESTEK_STATUS.PROPOSED || status === BESTEK_STATUS.ANSWERED
  );
};

/**
 * Customer may open the link only once the invite mail went out (or an answer
 * exists). Publishing is only real when the customer got the link + PDF.
 */
const isBestekLiveForCustomer = (bestek) =>
  isBestekVisibleToCustomer(bestek) &&
  (hasBestekCustomerChoice(bestek) || Boolean(bestek?.customerEmailSentAt));

/** Error code when a second signer is too late (someone already confirmed). */
const BESTEK_ALREADY_ANSWERED = "BESTEK_ALREADY_ANSWERED";

/**
 * Error code when the customer tries to confirm while an incompleteness report
 * is open (also a stale tab): the bestek is on hold until service handles it.
 */
const BESTEK_ON_HOLD = "BESTEK_ON_HOLD";

/** Error code when a mandatory PO / customer reference is missing. */
const BESTEK_REFERENCE_REQUIRED = "BESTEK_REFERENCE_REQUIRED";

/** Error code when the invite mail failed; nothing was published or changed. */
const BESTEK_EMAIL_FAILED = "BESTEK_EMAIL_FAILED";

/** Customer remark when the quote looks incomplete. Not a repair / no-repair decision. */
const BESTEK_INCOMPLETENESS_COMMENT_MAX_LENGTH = 2000;

/** Customer-facing bestek is valid for one calendar month after propose. */
const BESTEK_VALIDITY_MONTHS = 1;

const addBestekValidityMonths = (fromDate, months = BESTEK_VALIDITY_MONTHS) => {
  const d = new Date(fromDate);
  if (Number.isNaN(d.getTime())) return null;
  d.setMonth(d.getMonth() + Number(months || BESTEK_VALIDITY_MONTHS));
  return d;
};

/** Effective expiry: stored expiresAt, or submittedAt + 1 month for legacy docs. */
const getBestekExpiresAt = (bestek) => {
  if (!bestek) return null;
  if (bestek.expiresAt) {
    const stored = new Date(bestek.expiresAt);
    return Number.isNaN(stored.getTime()) ? null : stored;
  }
  if (bestek.submittedAt) {
    return addBestekValidityMonths(bestek.submittedAt);
  }
  return null;
};

/** Expired only blocks unanswered proposed besteks. */
const isBestekExpired = (bestek) => {
  if (hasBestekCustomerChoice(bestek)) return false;
  const expiresAt = getBestekExpiresAt(bestek);
  if (!expiresAt) return false;
  return Date.now() > expiresAt.getTime();
};

/**
 * FinERP wait status/substatus when proposing a bestek to the customer.
 * Keeps supplier path (4) if HRB is already there; otherwise intern (2).
 */
const getBestekProposeWaitTarget = (bestek, hrb = {}) => {
  const currentStatus = Number(hrb.HERSTATUSNR);
  const repairable = bestek?.outcome !== BESTEK_OUTCOME.UNREPAIRABLE;
  if (currentStatus === 4) {
    return {
      status: 4,
      subStatus: repairable ? 20 : 15,
    };
  }
  return {
    status: 2,
    subStatus: repairable ? 15 : 10,
  };
};

/**
 * FinERP status after customer answered.
 * Keeps supplier path (4) if HRB is already there; otherwise intern (2).
 * Repair = mag hersteld worden; anything else = niet herstellen.
 */
const getBestekAnsweredTarget = (bestek, hrb = {}) => {
  const currentStatus = Number(hrb.HERSTATUSNR);
  const decision = bestek?.customerChoice?.decision;
  const repair = decision === BESTEK_DECISION.REPAIR;
  if (currentStatus === 4) {
    return {
      status: 4,
      subStatus: repair ? 25 : 30,
    };
  }
  return {
    status: 2,
    subStatus: repair ? 20 : 25,
  };
};

module.exports = {
  BESTEK_STATUS,
  BESTEK_STATUS_LABELS,
  BESTEK_ANSWERED_TAG,
  BESTEK_INCOMPLETE_TAG,
  BESTEK_OUTCOME,
  BESTEK_DECISION,
  BESTEK_RAMING_ACTION,
  BESTEK_DISPOSITION,
  BESTEK_LINE_REQUIREMENT,
  BESTEK_RAMUTYPE,
  BESTEK_RAMUTYPE_TO_REQUIREMENT,
  BESTEK_TYPE,
  BESTEK_TYPE_LABELS,
  getBestekType,
  isRepairedBestekType,
  mapRamuTypeToLineRequirement,
  BESTEK_LINE_REQUIREMENT_LABELS,
  BESTEK_LINE_REQUIREMENT_DESCRIPTIONS,
  BESTEK_LINE_REQUIREMENT_BADGE,
  BESTEK_INCOMPLETENESS_COMMENT_MAX_LENGTH,
  BESTEK_VALIDITY_MONTHS,
  BESTEK_WAIT_STATUSES,
  BESTEK_ANSWERED_STATUSES,
  BESTEK_CREATE_STATUS,
  BESTEK_SUPPLIER_CREATE_STATUS,
  BESTEK_CREATE_STATUSES,
  getBestekCreateTarget,
  isBestekWaitStatus,
  isBestekAnsweredStatus,
  isBestekCreateStatus,
  hasBestekCustomerChoice,
  hasOpenBestekIncompleteness,
  isBestekContentLocked,
  formatBestekDocumentRef,
  getBestekStatus,
  isBestekVisibleToCustomer,
  isBestekLiveForCustomer,
  BESTEK_ALREADY_ANSWERED,
  BESTEK_ON_HOLD,
  BESTEK_REFERENCE_REQUIRED,
  BESTEK_EMAIL_FAILED,
  addBestekValidityMonths,
  getBestekExpiresAt,
  isBestekExpired,
  getBestekRequirementBadgeProps,
  getBestekProposeWaitTarget,
  getBestekAnsweredTarget,
};
